import { FX_ONE } from '../engine/simulation/fixed';
import { defaultConfig, type GameConfig, type Simulation } from '../engine/simulation/simulation';
import { kindIndex } from '../gameplay/registry';

/**
 * Repeatable benchmark scenario: a four-way battle of N units in the map centre.
 * Open the game with ?bench=500 to run it (camera centred, map revealed).
 */
export function benchConfig(): GameConfig {
  return defaultConfig({
    seed: 5, mapType: 'openPlains', mapSize: 'medium', revealMap: true,
    players: [0, 1, 2, 3].map((i) => ({ name: `Army ${i + 1}`, civ: ['english', 'french', 'ottomans', 'japanese'][i], team: -1 })),
  });
}

export function setupBench(sim: Simulation, units: number): void {
  const w = sim.world;
  for (const p of w.players) p.age = 4;
  const keys = ['swordsman', 'pikeman', 'crossbowman', 'knight', 'handCannoneer', 'lightCavalry', 'archer', 'mangonel'];
  const c = (w.size / 2) * FX_ONE;
  const per = Math.floor(units / 4);
  let seq = 900000;
  for (let p = 0; p < 4; p++) {
    const ids: number[] = [];
    const ox = [-1, 1, 0, 0][p] * 10 * FX_ONE;
    const oy = [0, 0, -1, 1][p] * 10 * FX_ONE;
    for (let i = 0; i < per; i++) {
      const s = w.spawn(kindIndex(keys[i % keys.length]), p, c + ox + (i % 12) * 650 - 3900, c + oy + Math.trunc(i / 12) * 650 - 3900);
      if (s >= 0) ids.push(w.idOf(s));
    }
    for (let i = 0; i < ids.length; i += 200) sim.schedule({ tick: sim.tick + 40, player: p, seq: seq++, cmd: { t: 'move', ids: ids.slice(i, i + 200), x: c, y: c, attackMove: true } });
  }
}

/** Architecture showcase (?showcase=<civ>&age=<0-4>): every building type of a civilisation. */
export function showcaseConfig(civ: string): GameConfig {
  return defaultConfig({ seed: 11, mapType: 'openPlains', mapSize: 'small', revealMap: true, players: [{ name: 'Showcase', civ, team: -1 }, { name: 'Other', civ: 'english', team: -1 }] });
}

export function setupShowcase(sim: Simulation, age: number): void {
  const w = sim.world;
  const p = w.players[0];
  p.age = age;
  const keys = ['townCenter', 'house', 'barracks', 'archeryRange', 'stable', 'blacksmith', 'market', 'mill', 'lumberCamp', 'miningCamp', 'farm', 'watchtower', 'guardTower', 'castle', 'university', 'monastery', 'siegeWorkshop', 'arsenal', 'foundry', 'wonder'];
  const c = Math.floor(w.size / 2);
  let x = c - 16;
  let y = c - 10;
  // Clear resources in the showcase area.
  for (let s = 0; s < w.highSlot; s++) if (w.alive[s] && w.cat[s] === 2 && Math.abs((w.x[s] >> 10) - c) < 22 && Math.abs((w.y[s] >> 10) - c) < 16) w.kill(s);
  for (const k of keys) {
    const ki = kindIndex(k);
    const size = 5;
    w.spawnBuilding(ki, 0, x, y, true);
    x += size + 1;
    if (x > c + 14) {
      x = c - 16;
      y += 7;
    }
  }
  const units = ['villager', 'militia', 'spearman', 'archer', 'knight', 'crossbowman', 'handCannoneer', 'lineInfantry', 'mangonel', 'trebuchet', 'bombard', 'ram'];
  units.forEach((u, i) => w.spawn(kindIndex(u), 0, (c - 14 + i * 2) * 1024, (c + 13) * 1024));
}
